import React, { useState } from "react";
import {
    Box,
    Button,
    TextField,
    Typography,
    RadioGroup,
    FormControlLabel,
    Radio,
    Grid,
    Dialog,
    DialogTitle,
    DialogContent,
    IconButton,
    Divider,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import axios from "axios";
import moment from "moment";
import { toast } from "react-toastify";
import { BaseUrl, ImageBaseUrl } from "../../../Environment";
import { getSecureItem } from "../../../utilsFunctions";
import { useEffect } from "react";
const KYC_API_URL = `${ImageBaseUrl}api/kyc-verification`;

const DOCTYPES = [
    { label: "Aadhaar", value: "aadhar" },
    { label: "PAN", value: "pan" },
    { label: "Bank", value: "bank" },
    { label: "Driving Licence", value: "dl" },
];

const validators = {
    accountNo: (acc) => /^[0-9]+$/.test(acc),
    ifsc: (ifsc) => /^[A-Za-z]{4}0[A-Za-z0-9]{6}$/.test(ifsc),
    pan: (pan) => /^[A-Za-z]{5}[0-9]{4}[A-Za-z]{1}$/.test(pan),
    dl: (dl) => /^[A-Za-z]{2}\d{2,3}\d{4}\d{6,7}$/.test(dl),
};

export default function KycVerification({
    open,
    onClose,
    customerId,
    kycDetails,
    kycVerified,
    onSuccess,
}) {
    const [kycType, setKycType] = useState("");
    const [step, setStep] = useState("select");
    const [loading, setLoading] = useState(false);

    const [otpSent, setOtpSent] = useState(false);
    const [refId, setRefId] = useState("");

    const [aadhar, setAadhar] = useState("");
    const [otp, setOtp] = useState("");

    const [pan, setPan] = useState("");
    const [panName, setPanName] = useState("");

    const [bank, setBank] = useState({ account: "", ifsc: "" });
    const [dl, setDl] = useState({ number: "", dob: "" });

    const [result, setResult] = useState(null);

    useEffect(() => {
        if (open && kycVerified && kycDetails?.length) {
            setResult(
                typeof kycDetails === "string" ? JSON.parse(kycDetails) : kycDetails,
            );
            setStep("result");
        }
    }, [open, kycVerified, kycDetails]);

    const resetAllState = () => {
        setKycType("");
        setStep("select");
        setOtpSent(false);
        setRefId("");
        setResult(null);
        setAadhar("");
        setOtp("");
        setPan("");
        setPanName("");
        setBank({ account: "", ifsc: "" });
        setDl({ number: "", dob: "" });
    };

    const handleClose = () => {
        resetAllState();
        onClose();
    };

    const resetState = () => {
        setOtpSent(false);
        setResult(null);
        setAadhar("");
        setOtp("");
        setPan("");
        setPanName("");
        setBank({ account: "", ifsc: "" });
        setDl({ number: "", dob: "" });
    };

    const handleTypeChange = (value) => {
        setKycType(value);
        resetState();
    };

    const getIdentifierCode = (type) => {
        const map = { aadhar: 1, pan: 2, bank: 3, dl: 4 };
        return map[type];
    };

    const callApi = async (url, payload) => {
        try {
            setLoading(true);

            const res = await axios.post(url, payload, {
                headers: {
                    Authorization: "Bearer " + getSecureItem("token"),
                },
            });

            if (res.data?.status) {
                toast.success(res.data.msg || "Success");
                return res.data;
            } else {
                const errMsg =
                    res.data?.error?.message || res.data?.msg || "Something went wrong";
                toast.error(errMsg);
            }
        } catch (err) {
            const errMsg =
                err.response?.data?.error?.message ||
                err.response?.data?.msg ||
                "Something went wrong";
            toast.error(errMsg);
        } finally {
            setLoading(false);
        }
    };

    const updateKYCStatus = async (data) => {
        try {
            await axios.put(
                `${BaseUrl}/customer/verifyCustomer`,
                {
                    customerId,
                    kycVerified: getIdentifierCode(kycType),
                    kycDetails: JSON.stringify(data),
                },
                { headers: { Authorization: "Bearer " + getSecureItem("token") } },
            );
        } catch (err) {
            console.log(err);
        }
    };

    const sendOtp = async () => {
        if (aadhar.length !== 12) return toast.warning("Aadhaar must be 12 digits");
        const res = await callApi(`${KYC_API_URL}/send-aadhaar-otp`, {
            aadhaar_number: aadhar,
        });
        if (res) {
            setRefId(res.data.ref_id);
            setOtpSent(true);
        }
    };

    const verifyOtp = async () => {
        if (otp.length !== 6) return toast.warning("OTP must be 6 digits");
        const res = await callApi(`${KYC_API_URL}/verify-aadhaar-otp`, {
            otp,
            ref_id: refId,
        });
        if (res) {
            const { name, dob, gender, address } = res.data;
            const formatted = [
                { label: "Name", value: name },
                { label: "DOB", value: dob },
                { label: "Gender", value: gender },
                { label: "Address", value: address },
            ];
            setResult(formatted);
            await updateKYCStatus(formatted);
            setStep("result");
            if (onSuccess) {
                onSuccess();
            }
        }
    };

    const verifyPan = async () => {
        if (!validators.pan(pan)) return toast.warning("Invalid PAN");
        const res = await callApi(`${KYC_API_URL}/verify-pan`, {
            pan,
            name: panName,
        });
        if (res) {
            const { registered_name, pan: p, type } = res.data;
            const formatted = [
                { label: "Name", value: registered_name },
                { label: "PAN", value: p },
                { label: "Type", value: type },
            ];
            setResult(formatted);
            await updateKYCStatus(formatted);
            setStep("result");
            if (onSuccess) {
                onSuccess();
            }
        }
    };

    const verifyBank = async () => {
        if (!validators.accountNo(bank.account))
            return toast.warning("Invalid Account");
        if (!validators.ifsc(bank.ifsc)) return toast.warning("Invalid IFSC");
        const res = await callApi(`${KYC_API_URL}/verify-bank`, {
            bank_account: bank.account,
            ifsc: bank.ifsc,
        });
        if (res) {
            const d = res.data;
            const formatted = [
                { label: "Name", value: d.name_at_bank },
                { label: "Bank", value: d.bank_name },
                { label: "Branch", value: d.branch },
                { label: "IFSC", value: d.ifsc_details?.ifsc },
            ];
            setResult(formatted);
            await updateKYCStatus(formatted);
            setStep("result");
            if (onSuccess) {
                onSuccess();
            }
        }
    };

    const verifyDl = async () => {
        if (!validators.dl(dl.number)) return toast.warning("Invalid DL");
        const res = await callApi(`${KYC_API_URL}/verify-dl`, {
            dl_number: dl.number,
            dob: moment(dl.dob).format("YYYY-MM-DD"),
        });
        if (res) {
            const d = res.data;
            const formatted = [
                { label: "DL Number", value: d.dl_number },
                { label: "Name", value: d.details_of_driving_licence?.name },
                { label: "DOB", value: d.dob },
            ];
            setResult(formatted);
            await updateKYCStatus(formatted);
            setStep("result");
            if (onSuccess) {
                onSuccess();
            }
        }
    };

    const renderInputs = () => {
        switch (kycType) {
            case "aadhar":
                return !otpSent ? (
                    <Box display="flex" flexDirection="column" gap={2}>
                        <TextField
                            label="Aadhaar Number"
                            fullWidth
                            inputProps={{ maxLength: 12 }}
                            value={aadhar}
                            onChange={(e) => setAadhar(e.target.value.replace(/\D/g, ""))}
                        />
                        <Button
                            fullWidth
                            variant="contained"
                            onClick={sendOtp}
                            disabled={loading}
                        >
                            {loading ? "Sending..." : "Send OTP"}
                        </Button>
                    </Box>
                ) : (
                    <Box display="flex" flexDirection="column" gap={2}>
                        <TextField
                            label="Enter OTP"
                            fullWidth
                            inputProps={{ maxLength: 6 }}
                            value={otp}
                            onChange={(e) => setOtp(e.target.value.replace(/\D/g, ""))}
                        />
                        <Button
                            fullWidth
                            variant="contained"
                            onClick={verifyOtp}
                            disabled={loading}
                        >
                            {loading ? "Verifying..." : "Verify OTP"}
                        </Button>
                        <Button
                            fullWidth
                            variant="text"
                            size="small"
                            onClick={() => setOtpSent(false)}
                        >
                            Change Aadhaar Number
                        </Button>
                    </Box>
                );

            case "pan":
                return (
                    <Box display="flex" flexDirection="column" gap={2}>
                        <TextField
                            label="PAN Number"
                            fullWidth
                            inputProps={{
                                maxLength: 10,
                                style: { textTransform: "uppercase" },
                            }}
                            value={pan}
                            onChange={(e) => setPan(e.target.value.toUpperCase())}
                        />
                        <TextField
                            label="Name as on PAN"
                            fullWidth
                            value={panName}
                            onChange={(e) => setPanName(e.target.value)}
                        />
                        <Button
                            fullWidth
                            variant="contained"
                            onClick={verifyPan}
                            disabled={loading}
                        >
                            {loading ? "Verifying..." : "Verify PAN"}
                        </Button>
                    </Box>
                );

            case "bank":
                return (
                    <Box display="flex" flexDirection="column" gap={2}>
                        <TextField
                            label="Account Number"
                            fullWidth
                            value={bank.account}
                            onChange={(e) =>
                                setBank({ ...bank, account: e.target.value.replace(/\D/g, "") })
                            }
                        />
                        <TextField
                            label="IFSC Code"
                            fullWidth
                            inputProps={{
                                maxLength: 11,
                                style: { textTransform: "uppercase" },
                            }}
                            value={bank.ifsc}
                            onChange={(e) =>
                                setBank({ ...bank, ifsc: e.target.value.toUpperCase() })
                            }
                        />
                        <Button
                            fullWidth
                            variant="contained"
                            onClick={verifyBank}
                            disabled={loading}
                        >
                            {loading ? "Verifying..." : "Verify Bank"}
                        </Button>
                    </Box>
                );

            case "dl":
                return (
                    <Box display="flex" flexDirection="column" gap={2}>
                        <TextField
                            label="DL Number"
                            fullWidth
                            inputProps={{ style: { textTransform: "uppercase" } }}
                            value={dl.number}
                            onChange={(e) =>
                                setDl({ ...dl, number: e.target.value.toUpperCase() })
                            }
                        />
                        <TextField
                            label="Date of Birth"
                            type="date"
                            fullWidth
                            InputLabelProps={{ shrink: true }}
                            value={dl.dob}
                            onChange={(e) => setDl({ ...dl, dob: e.target.value })}
                        />
                        <Button
                            fullWidth
                            variant="contained"
                            onClick={verifyDl}
                            disabled={loading}
                        >
                            {loading ? "Verifying..." : "Verify DL"}
                        </Button>
                    </Box>
                );

            default:
                return null;
        }
    };

    const renderResult = () => (
        <Box mt={1}>
            <Typography
                variant="h6"
                align="center"
                color="success.main"
                fontWeight="bold"
                sx={{ mb: 2 }}
            >
                ✅ Verification Successful
            </Typography>

            <Box
                sx={{
                    border: "1px solid",
                    borderColor: "divider",
                    borderRadius: 2,
                    overflow: "hidden",
                }}
            >
                {result?.map((item, i) => (
                    <React.Fragment key={i}>
                        <Box
                            sx={{
                                display: "flex",
                                alignItems: "flex-start",
                                px: 2,
                                py: 1.5,
                                backgroundColor: i % 2 === 0 ? "grey.50" : "background.paper",
                            }}
                        >
                            <Typography
                                fontWeight="600"
                                color="text.secondary"
                                sx={{ minWidth: 130, flexShrink: 0 }}
                            >
                                {item.label}
                            </Typography>
                            <Typography color="text.secondary" sx={{ mr: 1 }}>
                                :
                            </Typography>
                            <Typography color="text.primary" sx={{ wordBreak: "break-word" }}>
                                {item.value || "—"}
                            </Typography>
                        </Box>
                        {i < result.length - 1 && <Divider />}
                    </React.Fragment>
                ))}
            </Box>

            <Button fullWidth variant="outlined" sx={{ mt: 2 }} onClick={handleClose}>
                Done
            </Button>
        </Box>
    );

    return (
        <Dialog
            open={open}
            onClose={handleClose}
            fullWidth
            maxWidth="sm"
            disableEnforceFocus
            disableAutoFocus
            disableRestoreFocus
        >
            <DialogTitle
                sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    pr: 1,
                }}
            >
                <Typography variant="h6" fontWeight="bold">
                    KYC Verification
                </Typography>
                <IconButton onClick={handleClose} size="small">
                    <CloseIcon />
                </IconButton>
            </DialogTitle>

            <DialogContent dividers>
                {step === "select" && (
                    <Box display="flex" flexDirection="column" gap={2}>
                        <Typography variant="body2" color="text.secondary">
                            Select the document type to verify:
                        </Typography>

                        <RadioGroup
                            value={kycType}
                            onChange={(e) => handleTypeChange(e.target.value)}
                        >
                            {DOCTYPES.map((doc, idx) => (
                                <React.Fragment key={doc.value}>
                                    <FormControlLabel
                                        value={doc.value}
                                        control={<Radio size="small" />}
                                        label={<Typography variant="body1">{doc.label}</Typography>}
                                        sx={{ py: 0.5, m: 0 }}
                                    />
                                </React.Fragment>
                            ))}
                        </RadioGroup>

                        <Button
                            fullWidth
                            variant="contained"
                            disabled={!kycType}
                            onClick={() => setStep("form")}
                        >
                            Next
                        </Button>
                    </Box>
                )}

                {step === "form" && (
                    <Box display="flex" flexDirection="column" gap={2} mt={1}>
                        <Typography variant="body2" color="text.secondary">
                            Enter details for{" "}
                            <strong>
                                {DOCTYPES.find((d) => d.value === kycType)?.label}
                            </strong>{" "}
                            verification:
                        </Typography>

                        {renderInputs()}

                        <Button
                            fullWidth
                            variant="text"
                            color="inherit"
                            onClick={() => setStep("select")}
                        >
                            ← Back
                        </Button>
                    </Box>
                )}

                {step === "result" && renderResult()}
            </DialogContent>
        </Dialog>
    );
}
